import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import axios from 'axios'
import { apiClient, SESSION_RESTORED_EVENT } from '@/api/client'

/**
 * Device diagnostics for the restaurant tablets: a small, typed event log plus a
 * periodic status snapshot, batched and sent to the backend, which stores them in
 * CloudWatch. Only the fields typed below are ever sent: never order payloads,
 * customer names, phone numbers, addresses or printed text.
 */

export type DiagnosticsLevel = 'debug' | 'info' | 'warn' | 'error'
export type PrintJob = 'order' | 'tax_report' | 'cash_closing' | 'receipt'
type NoData = Record<string, never>

export interface DeviceEventData {
  'app.start': { gapSec: number | null }
  'app.resume': NoData
  'app.pause': NoData
  'auth.expired': NoData
  'auth.restored': NoData
  'net.offline': NoData
  'net.online': { offlineSec: number | null }
  'sse.connecting': { attempt: number }
  'sse.connected': { attempt: number; ms: number }
  'sse.error': { attempt: number; httpStatus?: number; error: string }
  'sse.watchdog': { silentMs: number }
  'sse.fallback_polling': { on: boolean }
  'order.received': { orderId: number; via: 'sse' | 'reconcile' | 'poll'; latencyMs: number }
  'order.alert_start': { orderId: number }
  'order.alert_stop': {
    orderId: number
    reason: 'accepted' | 'dismissed' | 'timeout' | 'changed_elsewhere'
    durationMs: number
  }
  'order.status_set': { orderId: number; status: string }
  'print.start': { job: PrintJob; orderId?: number; auto: boolean; copies?: number }
  'print.ok': { job: PrintJob; orderId?: number; auto: boolean; attempts?: number; durationMs: number }
  'print.fail': {
    job: PrintJob
    orderId?: number
    auto: boolean
    attempts?: number
    durationMs: number
    error: string
  }
  'power.change': { charging: boolean; level: number | null }
  'js.error': { message: string; stack?: string; source: 'onerror' | 'rejection' | 'boundary' }
  'diag.dropped': { count: number }
  'update.available': { bundle?: string; apk?: number; store?: string }
  'update.downloaded': { bundle?: string; apk?: number; ms: number }
  'update.installing': { apk: number; interactive: boolean }
  'update.applied': { from: string; to: string }
  'update.rollback': { bundle: string | null }
  'update.failed': { stage: 'check' | 'apply' | 'install'; error: string }
}

export type DeviceEventType = keyof DeviceEventData

/** Status parts that other features keep current, e.g. the order stream or the printer. */
export interface DeviceStatusParts {
  sse: { state: string; since: number; reconnects: number; fallbackPolling: boolean }
  orders: { lastReceivedId: number | null; lastReceivedAt: number | null; alerting: number }
  printer: {
    configured: boolean
    name?: string
    lastOkAt?: number | null
    lastFailAt?: number | null
    lastError?: string | null
  }
}

interface QueuedEvent {
  seq: number
  ts: number
  lvl: DiagnosticsLevel
  type: DeviceEventType
  msg?: string
  data?: object
}

interface DeviceInfo {
  id: string
  platform: string
  model?: string
  osVersion?: string
  webViewVersion?: string
  appVersion?: string
  appBuild?: string
  bundleVersion: string
}

interface BatteryState {
  level: number | null
  charging: boolean
}

interface BatteryManagerLike {
  level: number
  charging: boolean
}

const QUEUE_KEY_PREFIX = 'diag:queue:v1:'
const SEQ_KEY = 'diag:seq'
const DEVICE_ID_KEY = 'diag:device-id'
const ALIVE_KEY = 'diag:last-alive-at'

const MAX_QUEUE_EVENTS = 500
const MAX_QUEUE_CHARS = 256 * 1024
const MAX_BATCH = 200
const MIN_BATCH = 10
const MAX_MESSAGE_CHARS = 1000
const MAX_STACK_CHARS = 4000
const TICK_MS = 10_000
const FLUSH_INTERVAL_MS = 60_000
const HEARTBEAT_INTERVAL_MS = 5 * 60_000
const ERROR_FLUSH_DELAY_MS = 3_000
const MIN_REQUEST_GAP_MS = 10_000
const FIRST_BACKOFF_MS = 30_000
const MAX_BACKOFF_MS = 10 * 60_000
const SAVE_DEBOUNCE_MS = 2_000
const REPEATED_ERROR_WINDOW_MS = 60_000

interface Reporter {
  restaurantId: number
  device: DeviceInfo
  startedAt: number
  queue: QueuedEvent[]
  status: Partial<DeviceStatusParts>
  battery: BatteryState | null
  batchSize: number
  inFlight: boolean
  paused: boolean
  lastRequestAt: number
  lastFlushAt: number
  lastStatusAt: number
  statusRequested: boolean
  errorDueAt: number | null
  retryAt: number
  backoffMs: number
  offlineSince: number | null
  recentErrors: Map<string, number>
  saveTimer: ReturnType<typeof setTimeout> | null
  cleanups: (() => void)[]
}

let reporter: Reporter | null = null
let bootReported = false
// Bumped by every start and stop, so a start that is overtaken while awaiting device info gives up.
let generation = 0

export function isDeviceDiagnosticsSupported(): boolean {
  return Capacitor.isNativePlatform()
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Storage full or unavailable: the in-memory queue still works for this session.
  }
}

export function deviceId(): string {
  const existing = readStorage(DEVICE_ID_KEY)
  if (existing && /^[A-Za-z0-9_-]{8,64}$/.test(existing)) return existing
  const created =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '')
      : Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('')
  writeStorage(DEVICE_ID_KEY, created)
  return created
}

/** Model, Android version and WebView version from the WebView user agent. */
export function parseUserAgent(userAgent: string): Pick<DeviceInfo, 'model' | 'osVersion' | 'webViewVersion'> {
  const android = /Android ([\d.]+);\s*([^;)]+?)(?:\s+Build\/[^;)]*)?(?:;|\))/.exec(userAgent)
  const chrome = /Chrome\/([\d.]+)/.exec(userAgent)
  return {
    model: android?.[2].trim(),
    osVersion: android?.[1],
    webViewVersion: chrome?.[1],
  }
}

async function collectDevice(): Promise<DeviceInfo> {
  const app = await CapacitorApp.getInfo().catch(() => null)
  return {
    id: deviceId(),
    platform: Capacitor.getPlatform(),
    ...parseUserAgent(navigator.userAgent),
    appVersion: app?.version,
    appBuild: app?.build,
    bundleVersion: __BUNDLE_VERSION__,
  }
}

async function readBattery(): Promise<BatteryState | null> {
  const getBattery = (navigator as Navigator & { getBattery?: () => Promise<BatteryManagerLike> }).getBattery
  if (!getBattery) return null
  try {
    const battery = await getBattery.call(navigator)
    return { level: Number.isFinite(battery.level) ? battery.level : null, charging: battery.charging }
  } catch {
    return null
  }
}

function nextSeq(): number {
  const seq = (Number(readStorage(SEQ_KEY)) || 0) + 1
  writeStorage(SEQ_KEY, String(seq))
  return seq
}

// Per restaurant, so events never end up under another restaurant after a re-login.
function loadQueue(restaurantId: number): QueuedEvent[] {
  try {
    const parsed: unknown = JSON.parse(readStorage(`${QUEUE_KEY_PREFIX}${restaurantId}`) ?? '[]')
    return Array.isArray(parsed) ? (parsed as QueuedEvent[]).slice(-MAX_QUEUE_EVENTS) : []
  } catch {
    return []
  }
}

function saveQueue(current: Reporter) {
  if (current.saveTimer) clearTimeout(current.saveTimer)
  current.saveTimer = null
  writeStorage(`${QUEUE_KEY_PREFIX}${current.restaurantId}`, JSON.stringify(current.queue))
}

function scheduleSave(current: Reporter) {
  if (current.saveTimer) return
  current.saveTimer = setTimeout(() => saveQueue(current), SAVE_DEBOUNCE_MS)
}

/** Drop the oldest non-error events first so the reasons for a problem survive a long outage. */
function enforceLimits(current: Reporter) {
  let dropped = 0
  // Events are at most ~5 KB, so the size check only matters for longer queues.
  const tooBig = () =>
    current.queue.length > MAX_QUEUE_EVENTS ||
    (current.queue.length > 50 && JSON.stringify(current.queue).length > MAX_QUEUE_CHARS)
  while (current.queue.length > 1 && tooBig()) {
    const index = current.queue.findIndex((event) => event.lvl !== 'error' && event.type !== 'diag.dropped')
    current.queue.splice(index === -1 ? 0 : index, 1)
    dropped++
  }
  if (dropped > 0) {
    const marker = current.queue.find((event) => event.type === 'diag.dropped')
    if (marker) {
      marker.data = { count: ((marker.data as { count?: number } | undefined)?.count ?? 0) + dropped }
    } else {
      current.queue.unshift({ seq: nextSeq(), ts: Date.now(), lvl: 'warn', type: 'diag.dropped', data: { count: dropped } })
    }
  }
}

function enqueue(current: Reporter, event: Omit<QueuedEvent, 'seq' | 'ts'>) {
  current.queue.push({ seq: nextSeq(), ts: Date.now(), ...event })
  enforceLimits(current)
  if (event.lvl === 'error' && current.errorDueAt === null) {
    current.errorDueAt = Date.now() + ERROR_FLUSH_DELAY_MS
  }
  scheduleSave(current)
}

/** Record a diagnostics event. A no-op until the reporter runs (native app, restaurant session). */
export function trackDeviceEvent<T extends DeviceEventType>(
  type: T,
  data?: DeviceEventData[T],
  lvl: DiagnosticsLevel = 'info',
  msg?: string,
) {
  if (!reporter) return
  enqueue(reporter, {
    type,
    lvl,
    ...(msg ? { msg: msg.slice(0, MAX_MESSAGE_CHARS) } : {}),
    ...(data && Object.keys(data).length ? { data } : {}),
  })
}

// Kept outside the reporter so status set before it starts (e.g. the order stream connecting first) is not lost.
const latestStatus: Partial<DeviceStatusParts> = {}

/** Keep part of the status snapshot current; it is sent with every heartbeat. */
export function setDeviceStatus<K extends keyof DeviceStatusParts>(key: K, value: DeviceStatusParts[K]) {
  latestStatus[key] = value
  if (reporter) reporter.status[key] = value
}

function errorText(error: unknown): string {
  return (error instanceof Error ? error.message : String(error)).slice(0, MAX_MESSAGE_CHARS)
}

/** Runs one print job and records print.start / print.ok / print.fail plus the printer status. */
export async function trackPrint<T>(
  job: PrintJob,
  info: { orderId?: number; auto?: boolean; copies?: number },
  run: () => Promise<T>,
): Promise<T> {
  const base = { job, ...(info.orderId !== undefined ? { orderId: info.orderId } : {}), auto: info.auto ?? false }
  const printer = (): DeviceStatusParts['printer'] => ({
    lastOkAt: null,
    lastFailAt: null,
    lastError: null,
    ...latestStatus.printer,
    configured: readStorage('printer_device_address') !== null,
  })
  trackDeviceEvent('print.start', { ...base, ...(info.copies !== undefined ? { copies: info.copies } : {}) })
  const startedAt = Date.now()
  try {
    const result = await run()
    trackDeviceEvent('print.ok', { ...base, durationMs: Date.now() - startedAt })
    setDeviceStatus('printer', { ...printer(), lastOkAt: Date.now() })
    return result
  } catch (error) {
    const message = errorText(error)
    trackDeviceEvent('print.fail', { ...base, durationMs: Date.now() - startedAt, error: message }, 'error')
    setDeviceStatus('printer', { ...printer(), lastFailAt: Date.now(), lastError: message })
    throw error
  }
}

export function trackDeviceError(error: unknown, source: DeviceEventData['js.error']['source']) {
  const current = reporter
  if (!current) return
  const message = (error instanceof Error ? `${error.name}: ${error.message}` : String(error)).slice(
    0,
    MAX_MESSAGE_CHARS,
  )
  // A render loop or a failing interval would otherwise fill the queue with one error.
  const lastSeen = current.recentErrors.get(message)
  const now = Date.now()
  if (lastSeen !== undefined && now - lastSeen < REPEATED_ERROR_WINDOW_MS) return
  current.recentErrors.set(message, now)
  if (current.recentErrors.size > 50) current.recentErrors.clear()

  const stack = error instanceof Error && error.stack ? error.stack.slice(0, MAX_STACK_CHARS) : undefined
  trackDeviceEvent('js.error', { message, ...(stack ? { stack } : {}), source }, 'error')
}

function statusSnapshot(current: Reporter) {
  return {
    startedAt: current.startedAt,
    uptimeSec: Math.round((Date.now() - current.startedAt) / 1000),
    visibility: document.visibilityState,
    online: navigator.onLine,
    route: window.location.pathname,
    queued: current.queue.length,
    battery: current.battery,
    ...current.status,
  }
}

async function flush(current: Reporter) {
  const now = Date.now()
  const includeStatus = current.statusRequested || now - current.lastStatusAt >= HEARTBEAT_INTERVAL_MS
  const events = current.queue.slice(0, current.batchSize)
  if (!events.length && !includeStatus) return

  current.inFlight = true
  current.lastRequestAt = now
  current.statusRequested = false
  try {
    await apiClient.post(
      `/api/admin/restaurants/${current.restaurantId}/device-events`,
      {
        device: current.device,
        sentAt: Date.now(),
        ...(includeStatus ? { status: statusSnapshot(current) } : {}),
        events,
      },
      { timeout: 15_000 },
    )
    const lastSentSeq = events.length ? events[events.length - 1].seq : 0
    current.queue = current.queue.filter((event) => event.seq > lastSentSeq)
    current.lastFlushAt = Date.now()
    if (includeStatus) current.lastStatusAt = Date.now()
    current.backoffMs = 0
    current.retryAt = 0
    if (!current.queue.some((event) => event.lvl === 'error')) current.errorDueAt = null
    saveQueue(current)
  } catch (error) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined
    if (includeStatus) current.statusRequested = true
    if (status === 400) {
      // Permanently rejected (malformed); keeping it would block the queue forever.
      const lastSentSeq = events.length ? events[events.length - 1].seq : 0
      current.queue = current.queue.filter((event) => event.seq > lastSentSeq)
      saveQueue(current)
    } else if (status === 413) {
      current.batchSize = Math.max(MIN_BATCH, Math.floor(current.batchSize / 2))
    } else if (status === 401 || status === 403) {
      if (!current.paused) trackDeviceEvent('auth.expired', undefined, 'warn')
      current.paused = true
    } else {
      const retryAfterSec = axios.isAxiosError(error) ? Number(error.response?.headers['retry-after']) : NaN
      current.backoffMs = Number.isFinite(retryAfterSec)
        ? retryAfterSec * 1000
        : Math.min(current.backoffMs ? current.backoffMs * 2 : FIRST_BACKOFF_MS, MAX_BACKOFF_MS)
      current.retryAt = Date.now() + current.backoffMs
    }
  } finally {
    current.inFlight = false
  }
}

function tick(current: Reporter) {
  const now = Date.now()
  writeStorage(ALIVE_KEY, String(now))
  void readBattery().then((battery) => {
    if (battery && current.battery && battery.charging !== current.battery.charging) {
      trackDeviceEvent('power.change', battery)
    }
    current.battery = battery
  })

  if (current.inFlight || current.paused || now < current.retryAt || now - current.lastRequestAt < MIN_REQUEST_GAP_MS) {
    return
  }
  const due =
    current.statusRequested ||
    now - current.lastStatusAt >= HEARTBEAT_INTERVAL_MS ||
    (current.errorDueAt !== null && now >= current.errorDueAt) ||
    (current.queue.length > 0 && now - current.lastFlushAt >= FLUSH_INTERVAL_MS)
  if (due) void flush(current)
}

function requestFlush(current: Reporter) {
  current.statusRequested = true
  current.retryAt = 0
  tick(current)
}

/**
 * Start reporting for the signed-in restaurant. Returns a stop function. Calling it
 * again for another restaurant replaces the running reporter.
 */
export async function startDeviceDiagnostics(restaurantId: number): Promise<() => void> {
  stopDeviceDiagnostics()
  const startGeneration = ++generation
  const [device, battery] = await Promise.all([collectDevice(), readBattery()])
  if (startGeneration !== generation) return () => undefined
  const now = Date.now()
  const current: Reporter = {
    restaurantId,
    device,
    startedAt: now,
    queue: loadQueue(restaurantId),
    status: { ...latestStatus },
    battery,
    batchSize: MAX_BATCH,
    inFlight: false,
    paused: false,
    lastRequestAt: 0,
    lastFlushAt: 0,
    lastStatusAt: 0,
    statusRequested: true,
    errorDueAt: null,
    retryAt: 0,
    backoffMs: 0,
    offlineSince: navigator.onLine ? null : now,
    recentErrors: new Map(),
    saveTimer: null,
    cleanups: [],
  }
  reporter = current

  if (!bootReported) {
    bootReported = true
    const lastAlive = Number(readStorage(ALIVE_KEY))
    trackDeviceEvent('app.start', { gapSec: lastAlive > 0 ? Math.round((now - lastAlive) / 1000) : null })
  }

  const listen = <K extends keyof WindowEventMap>(type: K, handler: (event: WindowEventMap[K]) => void) => {
    window.addEventListener(type, handler)
    current.cleanups.push(() => window.removeEventListener(type, handler))
  }
  listen('error', (event) => trackDeviceError(event.error ?? event.message, 'onerror'))
  listen('unhandledrejection', (event) => trackDeviceError(event.reason, 'rejection'))
  listen('offline', () => {
    current.offlineSince = Date.now()
    trackDeviceEvent('net.offline', undefined, 'warn')
  })
  listen('online', () => {
    const offlineSec = current.offlineSince ? Math.round((Date.now() - current.offlineSince) / 1000) : null
    current.offlineSince = null
    trackDeviceEvent('net.online', { offlineSec })
    requestFlush(current)
  })
  const onSessionRestored = () => {
    if (current.paused) trackDeviceEvent('auth.restored')
    current.paused = false
    requestFlush(current)
  }
  window.addEventListener(SESSION_RESTORED_EVENT, onSessionRestored)
  current.cleanups.push(() => window.removeEventListener(SESSION_RESTORED_EVENT, onSessionRestored))
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') saveQueue(current)
  }
  document.addEventListener('visibilitychange', onVisibility)
  current.cleanups.push(() => document.removeEventListener('visibilitychange', onVisibility))

  void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    trackDeviceEvent(isActive ? 'app.resume' : 'app.pause')
    if (isActive) requestFlush(current)
    else saveQueue(current)
  })
    .then((handle) => {
      if (reporter === current) current.cleanups.push(() => void handle.remove())
      else void handle.remove()
    })
    .catch(() => undefined)

  const interval = setInterval(() => tick(current), TICK_MS)
  current.cleanups.push(() => clearInterval(interval))
  tick(current)

  return () => {
    if (reporter === current) stopDeviceDiagnostics()
  }
}

export function stopDeviceDiagnostics() {
  generation++
  const current = reporter
  if (!current) return
  reporter = null
  current.cleanups.forEach((cleanup) => cleanup())
  saveQueue(current)
}
